/**
 * useNavGuard — cambios sin guardar al pasar de ficha o cerrarla.
 *
 * El problema: la ficha sabe si hay cambios (`isDirty`) y cómo guardarlos,
 * pero la navegación la dispara el paginador o el botón «volver». Este hook
 * junta las dos cosas:
 *
 *   const nav = useNavGuard({
 *     dirty: isDirty || nameDirty,
 *     saving,
 *     save: () => handleSubmit(null, { force: true, keepEditing: editing }),
 *     entity: 'este servidor',
 *   });
 *
 *   // Al pulsar ‹ o ›:
 *   nav.request(() => onNavigate?.(destino));
 *   // Al pulsar «volver»:
 *   nav.request(() => onClose?.());
 *   // En el JSX: {nav.dialog}
 *
 * `save` TIENE que devolver una promesa con `true`/`false` (false = no se
 * guardó: validación, error del backend…). Solo con `true` se ejecuta la
 * acción pendiente, para no navegar con datos a medias.
 */
import React, { useCallback, useState } from 'react';
import UnsavedChangesDialog from '../components/common/UnsavedChangesDialog';

export default function useNavGuard({
  dirty = false,
  saving = false,
  save,
  entity = 'este registro',
  actionLabel = 'continúas',
}) {
  // `pending` es la acción que se ejecutará al resolver el diálogo. Se guarda
  // SIEMPRE con updater (`setPending(() => run)`) porque una función pasada
  // directamente a setState se interpreta como updater y se ejecuta.
  const [pending, setPending] = useState(null);

  const request = useCallback((run) => {
    if (typeof run !== 'function') return;
    if (!dirty) { run(); return; }
    setPending(() => run);
  }, [dirty]);

  const cancel = useCallback(() => {
    if (saving) return;
    setPending(null);
  }, [saving]);

  const discard = useCallback(() => {
    if (saving) return;
    const run = pending;
    setPending(null);
    run?.();
  }, [pending, saving]);

  const saveAndRun = useCallback(async () => {
    const run = pending;
    if (!run || saving) return;
    const ok = await save?.();
    if (ok) {
      setPending(null);
      run();
    }
  }, [pending, save, saving]);

  const dialog = (
    <UnsavedChangesDialog
      open={Boolean(pending)}
      entity={entity}
      saving={saving}
      actionLabel={actionLabel}
      onSave={saveAndRun}
      onDiscard={discard}
      onCancel={cancel}
    />
  );

  return { request, dialog };
}
